import * as React from "react";
import { useTranslation } from "react-i18next";
import { CategoryIcon } from "./feature-category-icons";
import type { ProductAllFeaturesCategory } from "~/data/products/types";

interface FeatureDirectoryProps {
  categories: ProductAllFeaturesCategory[];
  onSelect: (slug: string) => void;
}

export function FeatureDirectory({ categories, onSelect }: FeatureDirectoryProps) {
  const { t } = useTranslation("products");

  function goTo(event: React.MouseEvent<HTMLAnchorElement>, slug: string, cardId?: string) {
    event.preventDefault();
    onSelect(slug);
    if (!cardId) {
      document.getElementById(slug)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    // Wait out the accordion's own 0.2s open animation so the card has its final position
    // before centering it, not wherever it sat mid-animation.
    setTimeout(() => {
      document.getElementById(cardId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      flashCard(cardId);
    }, 300);
  }

  function flashCard(id: string) {
    const holdMs = 2000;
    const fadeMs = 1500;
    const hold = holdMs / (holdMs + fadeMs);
    const card = document.getElementById(id);
    if (!card) return;
    // Bg-Secondary on the dark fleet page, where its light text would wash out on Morning Fog.
    const flash = card.closest(".fleet") ? "var(--fleet-surface-secondary)" : "var(--color-bouncie-100)";
    card.animate(
      [
        { backgroundColor: flash, offset: 0 },
        { backgroundColor: flash, offset: hold, easing: "ease-out" },
        { backgroundColor: "transparent", offset: 1 },
      ],
      { duration: holdMs + fadeMs },
    );
  }

  return (
    <section className="fleet:bg-transparent hidden bg-white pb-14 lg:block">
      <div className="wrap flex items-stretch gap-6">
        {categories.map(({ slug, icon, category }) => (
          <div key={slug} className="fleet:border-(--fleet-border-subtle) flex flex-1 flex-col gap-3 rounded-2xl border border-neutral-100 px-4 py-6">
            <div className="flex items-center gap-2">
              <span className="fleet:border-bg-alt fleet:bg-bg-alt flex size-8 shrink-0 items-center justify-center rounded-2xl border border-neutral-100">
                <CategoryIcon icon={icon} className="text-text-heading fleet:text-icon-primary size-5" />
              </span>
              <a
                href={`#${slug}`}
                onClick={(event) => goTo(event, slug)}
                data-sid={`all-features-directory-category:${slug}`}
                className="font-display text-text-heading text-md leading-[1.15] font-extrabold whitespace-pre-line hover:underline"
              >
                {t(category.label)}
              </a>
            </div>
            <ul className="fleet:border-(--fleet-border-subtle) flex flex-col gap-2 border-t border-neutral-100 pt-3">
              {/* leading-5 on the li, not just the <a> — kills the 24px list-item strut stretching wrapped lines. */}
              {category.cards.map((card) => (
                <li key={card.title} className="leading-5">
                  <a
                    href={`#${slug}`}
                    onClick={(event) => goTo(event, slug, card.title)}
                    data-sid={`all-features-directory-feature:${card.title}`}
                    className="text-bouncie-600 fleet:text-text-body-tertiary text-sm leading-5 hover:underline"
                  >
                    {t(card.title)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
